import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { X, Tags, MessageSquare, User, FolderOpen, Trash2, Pencil, List, Users } from 'lucide-react'
import type { RootState } from '../../store'
import type { ContactTag } from '../../types/conversation.types'
import { tagService, DEFAULT_CONTACT_TAG_COLOR } from '../../services/tagService'
import { conversationService } from '../../services/conversationService'
import { ConfirmActionModal } from '../common/ConfirmActionModal'
import { TagUsageExplorerModal } from './TagUsageExplorerModal'
import toast from 'react-hot-toast'
import { logger } from '../../utils/logger'

type WorkspaceTab = 'conversation' | 'client' | 'catalog'

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/

/**
 * Tags só com `admin_id` (`owner_user_id` nulo) são do catálogo da empresa — no painel, só admin edita/exclui.
 * Atendentes só alteram tags próprias (`owner_user_id` = utilizador atual).
 */
function canEditOrDeleteCatalogTag(
  tag: ContactTag,
  opts: { isAdmin: boolean; attendantUserId: number | undefined }
): boolean {
  if (opts.isAdmin) return true
  if (tag.owner_user_id == null) return false
  return tag.owner_user_id === opts.attendantUserId
}

export interface ContactTagsWorkspaceModalProps {
  isOpen: boolean
  onClose: () => void
  conversationId: number
  clientId: number
  onApplied: () => void | Promise<void>
  /** Chamado após criar/editar/eliminar tag no catálogo (atualizar filtros na página). */
  onCatalogMutated?: () => void | Promise<void>
  onOpenConversation?: (conversationId: number) => void
}

export const ContactTagsWorkspaceModal: React.FC<ContactTagsWorkspaceModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  clientId,
  onApplied,
  onCatalogMutated,
  onOpenConversation
}) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const isAdminUser = user?.type === 'admin'
  const attendantUserId = user?.type === 'user' ? user.id : undefined

  const [tab, setTab] = useState<WorkspaceTab>('conversation')
  const [catalog, setCatalog] = useState<ContactTag[]>([])
  const [convSelected, setConvSelected] = useState<number[]>([])
  const [clientSelected, setClientSelected] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(DEFAULT_CONTACT_TAG_COLOR)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState(DEFAULT_CONTACT_TAG_COLOR)
  const [deleteTarget, setDeleteTarget] = useState<ContactTag | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [explorer, setExplorer] = useState<{ tag: ContactTag; mode: 'conversations' | 'clients' } | null>(null)

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [tagsRes, convRes] = await Promise.all([
        tagService.listTags(),
        conversationService.getConversation(conversationId, { include_tags: true })
      ])
      if (tagsRes.success && Array.isArray(tagsRes.data)) {
        setCatalog(tagsRes.data)
      }
      if (convRes.success && convRes.data) {
        setConvSelected((convRes.data.tags || []).map(t => t.id))
        setClientSelected((convRes.data.client?.tags || []).map(t => t.id))
      }
    } catch (e) {
      logger.error('ContactTagsWorkspaceModal load:', e)
      toast.error('Erro ao carregar tags')
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    if (isOpen) {
      setTab('conversation')
      setEditingId(null)
      setDeleteTarget(null)
      loadAll()
    }
  }, [isOpen, loadAll])

  const toggleId = (id: number, setSelected: React.Dispatch<React.SetStateAction<number[]>>) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const saveConversationTags = async () => {
    setSaving(true)
    try {
      const res = await tagService.syncConversationTags(conversationId, convSelected)
      if (res.success) {
        toast.success(res.message || 'Tags da conversa atualizadas')
        await onApplied()
        await loadAll()
      } else {
        toast.error('Não foi possível salvar')
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Erro ao salvar tags da conversa')
    } finally {
      setSaving(false)
    }
  }

  const saveClientTags = async () => {
    setSaving(true)
    try {
      const res = await tagService.syncClientTags(clientId, clientSelected)
      if (res.success) {
        toast.success(res.message || 'Tags do contato atualizadas')
        await onApplied()
        await loadAll()
      } else {
        toast.error('Não foi possível salvar')
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Erro ao salvar tags do contato')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = newName.trim()
    if (!name) {
      toast.error('Nome da tag é obrigatório')
      return
    }
    const color = newColor.trim()
    if (!HEX_COLOR.test(color)) {
      toast.error('Cor inválida. Use #RRGGBB')
      return
    }
    setSaving(true)
    try {
      const res = await tagService.createTag(name, color)
      if (res.success) {
        toast.success(res.message || 'Tag criada')
        setNewName('')
        setNewColor(DEFAULT_CONTACT_TAG_COLOR)
        await loadAll()
        await onCatalogMutated?.()
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Erro ao criar tag')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (t: ContactTag) => {
    setEditingId(t.id)
    setEditName(t.name)
    setEditColor(t.color)
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const saveEdit = async (t: ContactTag) => {
    const name = editName.trim()
    if (!name) {
      toast.error('Nome obrigatório')
      return
    }
    const color = editColor.trim()
    if (!HEX_COLOR.test(color)) {
      toast.error('Cor inválida (#RRGGBB)')
      return
    }
    setSaving(true)
    try {
      const res = await tagService.updateTag(t.id, name, color)
      if (res.success) {
        toast.success(res.message || 'Tag atualizada')
        setEditingId(null)
        await loadAll()
        await onCatalogMutated?.()
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Erro ao atualizar')
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await tagService.deleteTag(deleteTarget.id)
      if (res.success) {
        toast.success(res.message || 'Tag removida')
        const removedId = deleteTarget.id
        setDeleteTarget(null)
        await loadAll()
        setConvSelected(prev => prev.filter(id => id !== removedId))
        setClientSelected(prev => prev.filter(id => id !== removedId))
        await onCatalogMutated?.()
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || 'Erro ao remover')
    } finally {
      setDeleting(false)
    }
  }

  if (!isOpen) return null

  const tabBtn = (id: WorkspaceTab, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      key={id}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
        tab === id
          ? 'bg-blue-50 border-blue-200 text-blue-800'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <>
      <div className="fixed inset-0 z-[50] flex items-center justify-center p-4 bg-black/50">
        <div
          className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col border border-gray-200"
          role="dialog"
          aria-labelledby="tags-workspace-title"
        >
          <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <Tags className="w-5 h-5 text-blue-600 shrink-0" />
              <h2 id="tags-workspace-title" className="text-lg font-semibold text-gray-900 truncate">
                Tags
              </h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Fechar">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 pt-3 flex flex-wrap gap-2">
            {tabBtn('conversation', 'Conversa', <MessageSquare className="w-4 h-4" />)}
            {tabBtn('client', 'Contato', <User className="w-4 h-4" />)}
            {tabBtn('catalog', 'Catálogo', <FolderOpen className="w-4 h-4" />)}
          </div>

          <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
            {loading ? (
              <p className="text-sm text-gray-500 text-center py-8">Carregando...</p>
            ) : tab === 'conversation' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Tags desta conversa (não alteram o cadastro do contato).</p>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                  {catalog.map(t => {
                    const active = convSelected.includes(t.id)
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleId(t.id, setConvSelected)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${active ? 'ring-2 ring-offset-1' : ''}`}
                        style={{ borderColor: t.color, backgroundColor: active ? `${t.color}22` : 'white' }}
                      >
                        {t.name}
                      </button>
                    )
                  })}
                </div>
                {catalog.length === 0 && <p className="text-sm text-gray-500">Crie tags no separador Catálogo.</p>}
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveConversationTags}
                  className="w-full py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'A guardar...' : 'Guardar tags da conversa'}
                </button>
              </div>
            ) : tab === 'client' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Tags do contato (aplicam-se a este cliente em todas as conversas).</p>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                  {catalog.map(t => {
                    const active = clientSelected.includes(t.id)
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleId(t.id, setClientSelected)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${active ? 'ring-2 ring-offset-1' : ''}`}
                        style={{ borderColor: t.color, backgroundColor: active ? `${t.color}22` : 'white' }}
                      >
                        {t.name}
                      </button>
                    )
                  })}
                </div>
                {catalog.length === 0 && <p className="text-sm text-gray-500">Crie tags no separador Catálogo.</p>}
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveClientTags}
                  className="w-full py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {saving ? 'A guardar...' : 'Guardar tags do contato'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <form onSubmit={handleCreateTag} className="space-y-2 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-sm font-medium text-gray-800">Nova tag</p>
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Nome"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    maxLength={80}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newColor.length === 7 ? newColor : DEFAULT_CONTACT_TAG_COLOR}
                      onChange={e => setNewColor(e.target.value)}
                      className="h-9 w-12 rounded border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={newColor}
                      onChange={e => setNewColor(e.target.value)}
                      placeholder="#RRGGBB"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="text-sm px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
                  >
                    Criar tag
                  </button>
                </form>

                <ul className="space-y-2">
                  {catalog.map(t => {
                    const canMutate = canEditOrDeleteCatalogTag(t, {
                      isAdmin: isAdminUser,
                      attendantUserId
                    })
                    return (
                    <li
                      key={t.id}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 rounded-lg border border-gray-100 hover:bg-gray-50/80"
                    >
                      {editingId === t.id && canMutate ? (
                        <div className="flex-1 space-y-2 w-full">
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={editColor.length === 7 ? editColor : DEFAULT_CONTACT_TAG_COLOR}
                              onChange={e => setEditColor(e.target.value)}
                              className="h-8 w-10 rounded border cursor-pointer"
                            />
                            <input
                              type="text"
                              value={editColor}
                              onChange={e => setEditColor(e.target.value)}
                              className="flex-1 px-2 py-1 text-xs font-mono border rounded"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(t)}
                              className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                            >
                              Guardar
                            </button>
                            <button type="button" onClick={cancelEdit} className="text-xs px-2 py-1 border rounded">
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="w-3 h-3 rounded-full shrink-0 border border-gray-200" style={{ backgroundColor: t.color }} />
                            <span className="text-sm font-medium text-gray-900 truncate">{t.name}</span>
                            {t.owner_user_id != null && (
                              <span className="text-[10px] text-gray-400 shrink-0">(privada)</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => setExplorer({ tag: t, mode: 'conversations' })}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                              title="Ver conversas"
                            >
                              <List className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setExplorer({ tag: t, mode: 'clients' })}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                              title="Ver clientes"
                            >
                              <Users className="w-4 h-4" />
                            </button>
                            {canMutate && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEdit(t)}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Editar"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteTarget(t)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Excluir tag"
        message={
          deleteTarget ? (
            <>
              Remover a tag <strong>{deleteTarget.name}</strong>? Os vínculos em conversas e clientes serão removidos.
            </>
          ) : null
        }
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        isLoading={deleting}
        icon={Trash2}
        variant="danger"
      />

      {explorer && (
        <TagUsageExplorerModal
          key={`${explorer.tag.id}-${explorer.mode}`}
          isOpen
          onClose={() => setExplorer(null)}
          tag={explorer.tag}
          mode={explorer.mode}
          onOpenConversation={onOpenConversation}
        />
      )}
    </>
  )
}
